import { Injectable, inject } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import * as ExcelJS from 'exceljs';
import {
	type ArcElement,
	type BarElement,
	Chart,
	registerables,
	type ChartConfiguration,
	type Plugin
} from 'chart.js';
import { type InvoiceSubmissionResult } from '../../models/invoice-submission';
import { type SoPendingBillingResult } from '../../models/so-pending-billing';

Chart.register(...registerables);

export interface ReportColumn {
	field: string;
	header: string;
}

interface DestinationCounts {
	saved: number;
	submit: number;
}

interface RequisitionerSummary {
	requisitioner: string;
	totalTickets: number;
	approved: number;
	submitted: number;
	disputed: number;
	openBalance: number;
}

interface InvoiceNeedingAttention {
	customerJobLocation: string;
	documentNumber: string;
	status: string;
	daysOutstanding: number;
	openBalance: number;
	requisitioner: string;
}

interface WeeklySummaryRow {
	weekStart: Date;
	succeeded: number;
	failed: number;
	total: number;
	amountProcessed: number;
	companyCounts: Record<string, number>;
	destinationTotals: DestinationCounts;
	successByDestination: DestinationCounts;
	failedByDestination: DestinationCounts;
}

export interface ReportRow {
	id: number;
	amount: number;
	customerJobLocation?: string;
	transactionType?: string;
	date?: string;
	documentNumber?: string;
	poNumber?: string;
	jobLocation?: string;
	dueDate?: string;
	rkSubmitter?: string;
	age?: string;
	openBalance?: string;
	memo?: string;
	soNumbers?: string;
	otStatus?: string;
	otSubDate?: string;
	otDisputedDate?: string;
	otDisputedResubmission?: boolean;
	otApprovedDate?: string;
	requisitioner?: string;
	otDisputeNote?: string;
	detailedNotes?: string;
	oiStatus?: string;
	oiSubDate?: string;
	oiStatusDate?: string;
	paidDate?: string;
	disputedNote?: string;
	otSubToOtDispDays?: string;
	rkInvToOtSubDays?: string;
	otSubToOtAppDays?: string;
	otAppToOiSubDays?: string;
	oiSubToOiAppDays?: string;
	oiSubToPaidDays?: string;
}

@Injectable()
export class ReportExportService {
	public static readonly REPORT_COLUMNS: ReportColumn[] = [
		{ field: 'customerJobLocation', header: 'Customer:Job/Location' },
		{ field: 'transactionType', header: 'Transaction Type' },
		{ field: 'date', header: 'Date' },
		{ field: 'documentNumber', header: 'Document Number' },
		{ field: 'poNumber', header: 'P.O. No.' },
		{ field: 'jobLocation', header: 'Job Location' },
		{ field: 'rkSubmitter', header: 'RK Submitter' },
		{ field: 'dueDate', header: 'Due Date' },
		{ field: 'age', header: 'Age' },
		{ field: 'openBalance', header: 'Open Balance' },
		{ field: 'memo', header: 'Memo' },
		{ field: 'soNumbers', header: 'SO Number(s)' },
		{ field: 'otStatus', header: 'OT Status' },
		{ field: 'otSubDate', header: 'OT Sub Date' },
		{ field: 'otDisputedDate', header: 'OT Disputed Date' },
		{ field: 'otDisputedResubmission', header: 'OT Dispute Resubmission?' },
		{ field: 'otApprovedDate', header: 'OT Approved Date' },
		{ field: 'requisitioner', header: 'Requisitioner' },
		{ field: 'otDisputeNote', header: 'OT Dispute /Note' },
		{ field: 'detailedNotes', header: 'Detailed Notes' },
		{ field: 'oiStatus', header: 'OI Status' },
		{ field: 'oiSubDate', header: 'OI Sub Date' },
		{ field: 'oiStatusDate', header: 'OI Status Date' },
		{ field: 'paidDate', header: 'Paid date' },
		{ field: 'disputedNote', header: 'Disputed Note' },
		{ field: 'otSubToOtDispDays', header: 'OT Sub date to OT Disp. Date' },
		{ field: 'rkInvToOtSubDays', header: 'RK Inv Date to OT Sub date' },
		{ field: 'otSubToOtAppDays', header: 'OT Sub date to OT App Date' },
		{ field: 'otAppToOiSubDays', header: 'OT App Date to OI Sub Date' },
		{ field: 'oiSubToOiAppDays', header: 'OI Sub date to OI App Date' },
		{ field: 'oiSubToPaidDays', header: 'OI Sub date to Paid Date' }
	];

	private static readonly EXPORT_FIELD_LAYOUT: (Exclude<
		keyof ReportRow,
		'id' | 'amount'
	> | null)[] = [
		'customerJobLocation',
		'transactionType',
		'date',
		'documentNumber',
		'poNumber',
		'jobLocation',
		'rkSubmitter',
		'dueDate',
		'age',
		'openBalance',
		'memo',
		'soNumbers',
		'otStatus',
		'otSubDate',
		'otDisputedDate',
		'otDisputedResubmission',
		'otApprovedDate',
		'requisitioner',
		'otDisputeNote',
		'detailedNotes',
		null,
		'oiStatus',
		'oiSubDate',
		'oiStatusDate',
		'paidDate',
		'disputedNote',
		null,
		'otSubToOtDispDays',
		'rkInvToOtSubDays',
		'otSubToOtAppDays',
		'otAppToOiSubDays',
		'oiSubToOiAppDays',
		'oiSubToPaidDays'
	];

	private static readonly EXPORT_HEADER_MERGES: [
		number,
		number,
		number,
		number
	][] = [
		[1, 1, 1, 11],
		[1, 21, 1, 22],
		[2, 1, 2, 11],
		[2, 21, 2, 22],
		[3, 1, 3, 11],
		[3, 21, 3, 22],
		[4, 1, 4, 11],
		[4, 21, 4, 22],
		[5, 1, 5, 11],
		[5, 12, 5, 19],
		[5, 21, 5, 25],
		[5, 27, 5, 32]
	];

	private static readonly EXPORT_STATUS_TABS: {
		name: string;
		color?: string;
		filter: (row: ReportRow) => boolean;
		summaryLabel: string;
		summaryColor: string;
		summaryBasis: string;
		summaryAvgDays: (row: ReportRow) => number | undefined;
	}[] = [
		{
			name: 'Open Ticket Approved',
			color: 'FFCCFFCC',
			filter: (row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOtStatus(row),
					'Approved'
				),
			summaryLabel: 'Open Ticket – Approved',
			summaryColor: 'FFCCFFCC',
			summaryBasis: 'Avg. turnaround: ticket submitted → approved',
			summaryAvgDays: (row) =>
				ReportExportService.parseDays(row.otSubToOtAppDays)
		},
		{
			name: 'Open Ticket Submitted',
			color: 'FFFFFFCC',
			filter: (row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOtStatus(row),
					'Submitted'
				),
			summaryLabel: 'Open Ticket – Submitted',
			summaryColor: 'FFFFFFCC',
			summaryBasis: 'Avg. days pending since ticket submitted',
			summaryAvgDays: (row) =>
				ReportExportService.daysSinceToday(row.otSubDate)
		},
		{
			name: 'Open Invoice Approved',
			color: 'FF92D050',
			filter: (row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOiStatus(row),
					'Approved'
				),
			summaryLabel: 'Open Invoice – Approved',
			summaryColor: 'FF92D050',
			summaryBasis: 'Avg. turnaround: invoice submitted → approved',
			summaryAvgDays: (row) =>
				ReportExportService.parseDays(row.oiSubToOiAppDays)
		},
		{
			name: 'Open Invoice Submitted',
			color: 'FFFFFF00',
			filter: (row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOiStatus(row),
					'Submitted'
				),
			summaryLabel: 'Open Invoice – Submitted',
			summaryColor: 'FFFFFF00',
			summaryBasis: 'Avg. days pending since invoice submitted',
			summaryAvgDays: (row) =>
				ReportExportService.daysSinceToday(row.oiSubDate)
		},
		{
			name: 'Disputed',
			color: 'FFFF0000',
			filter: (row) => ReportExportService.isDisputed(row),
			summaryLabel: 'Disputed',
			summaryColor: 'FFFFCCCC',
			summaryBasis: 'Avg. days open since flagged disputed',
			summaryAvgDays: (row) =>
				ReportExportService.daysSinceToday(
					row.otDisputedDate && row.otDisputedDate.length > 0
						? row.otDisputedDate
						: row.oiStatusDate
				)
		},
		{
			name: 'Saved and Not Submitted',
			filter: (row) =>
				!ReportExportService.effectiveOiStatus(row) &&
				(!row.otStatus ||
					ReportExportService.isStatus(row.otStatus, 'Saved')),
			summaryLabel: 'Saved / Not Submitted',
			summaryColor: 'FFCCFFFF',
			summaryBasis: 'Avg. days since last saved / created',
			summaryAvgDays: (row) =>
				ReportExportService.daysSinceToday(row.date)
		}
	];

	private static readonly EXPORT_COLUMN_MIN_WIDTH = 10;
	private static readonly EXPORT_COLUMN_WIDTH_PADDING = 2;

	private static readonly EXPORT_COLUMN_HEADER_FILL = 'FFD0D0D0';
	private static readonly SUMMARY_BANNER_FILL = 'FF8B0000';
	private static readonly SUMMARY_HEADER_FILL = 'FF1A1A1A';
	private static readonly SUMMARY_BAND_FILL = 'FFF2F2F2';
	private static readonly SUMMARY_COLUMN_WIDTHS = [30, 16, 12, 20, 40, 18];

	private static readonly SUMMARY_MASTHEAD_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 12,
		bold: true,
		color: { argb: 'FF808080' }
	};
	private static readonly SUMMARY_TITLE_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 18,
		bold: true,
		color: { argb: 'FF8B0000' }
	};
	private static readonly SUMMARY_SUBTITLE_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 12,
		bold: true,
		color: { argb: 'FF595959' }
	};
	private static readonly SUMMARY_ASOF_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 9,
		color: { argb: 'FF808080' }
	};
	private static readonly SUMMARY_BANNER_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 11,
		bold: true,
		color: { argb: 'FFFFFFFF' }
	};
	private static readonly SUMMARY_HEADER_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 11,
		bold: true,
		color: { argb: 'FFFFFFFF' }
	};
	private static readonly SUMMARY_DATA_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 10
	};
	private static readonly SUMMARY_BOLD_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 10,
		bold: true
	};
	private static readonly SUMMARY_BASIS_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 9,
		color: { argb: 'FF808080' }
	};
	private static readonly SUMMARY_FOOTNOTE_FONT: Partial<ExcelJS.Font> = {
		name: 'Calibri',
		size: 9,
		color: { argb: 'FF808080' }
	};

	private static readonly EXPORT_TITLE_FONT: Partial<ExcelJS.Font> = {
		name: 'Arial',
		size: 12,
		bold: true
	};
	private static readonly EXPORT_SECTION_FONT: Partial<ExcelJS.Font> = {
		name: 'Arial',
		size: 14,
		bold: true
	};
	private static readonly EXPORT_DAYS_HEADER_FONT: Partial<ExcelJS.Font> = {
		name: 'Arial',
		size: 12,
		bold: true
	};
	private static readonly EXPORT_LEGEND_FONT: Partial<ExcelJS.Font> = {
		name: 'Arial',
		size: 8
	};
	private static readonly EXPORT_CENTER_ALIGNMENT: Partial<ExcelJS.Alignment> =
		{ horizontal: 'center' };
	private static readonly EXPORT_CENTER_WRAP_ALIGNMENT: Partial<ExcelJS.Alignment> =
		{ horizontal: 'center', wrapText: true };
	private static readonly EXPORT_SECTION_BORDER: Partial<ExcelJS.Borders> = {
		top: { style: 'thin' },
		left: { style: 'thin' },
		bottom: { style: 'thin' }
	};

	private static readonly EXPORT_AGE_COLUMN = 9;
	private static readonly EXPORT_OPEN_BALANCE_COLUMN = 10;
	private static readonly EXPORT_COLUMN_TOTAL_FILL = 'FFFFFF00';
	private static readonly EXPORT_COLUMN_TOTAL_FONT: Partial<ExcelJS.Font> = {
		name: 'Arial',
		size: 9,
		bold: true,
		color: { argb: 'FFFF0000' }
	};

	private static readonly EXPORT_DAY_COUNT_FIELDS: ReadonlySet<string> =
		new Set([
			'otSubToOtDispDays',
			'rkInvToOtSubDays',
			'otSubToOtAppDays',
			'otAppToOiSubDays',
			'oiSubToOiAppDays',
			'oiSubToPaidDays'
		]);
	// AB:AG - matches EXPORT_FIELD_LAYOUT's position for the 6 day-count fields.
	private static readonly EXPORT_DAY_COUNT_COLUMNS = [28, 29, 30, 31, 32, 33];
	private static readonly EXPORT_DAY_COUNT_NUMBER_FORMAT = '0';

	private static readonly EXPORT_DATE_FIELDS: ReadonlySet<string> = new Set([
		'date',
		'dueDate',
		'otSubDate',
		'otDisputedDate',
		'otApprovedDate',
		'oiSubDate',
		'oiStatusDate',
		'paidDate'
	]);
	// C, H, N, O, Q, W, X, Y - matches EXPORT_FIELD_LAYOUT's position for the
	// 8 date fields (P/16 is 'OT Dispute Resubmission?', a boolean, not a date).
	private static readonly EXPORT_DATE_COLUMNS = [
		3, 8, 14, 15, 17, 23, 24, 25
	];
	private static readonly EXPORT_DATE_NUMBER_FORMAT = 'mm-dd-yy';

	private static readonly EXPORT_DATA_FONT: Partial<ExcelJS.Font> = {
		name: 'Arial',
		size: 8
	};
	private static readonly EXPORT_CURRENCY_NUMBER_FORMAT = '"$"#,##0.00';

	private static readonly EXPORT_INVOICE_BLOCK_COLUMNS = [
		1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11
	];

	private static readonly EXPORT_ROW_FILLS: {
		match: (row: ReportRow) => boolean;
		color: string;
		statusColumns: number[];
	}[] = [
		{
			match: (row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOiStatus(row),
					'Approved'
				),
			color: 'FF92D050',
			statusColumns: [22]
		},
		{
			match: (row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOiStatus(row),
					'Submitted'
				),
			color: 'FFFFFF00',
			statusColumns: [22]
		},
		{
			match: (row) => ReportExportService.isStatus(row.oiStatus, 'Saved'),
			color: 'FF00B0F0',
			statusColumns: [22]
		},
		{
			match: (row) =>
				ReportExportService.isStatus(row.oiStatus, 'Disputed'),
			color: 'FFFF0000',
			statusColumns: [22]
		},
		{
			match: (row) =>
				ReportExportService.isStatus(row.otStatus, 'Approved'),
			color: 'FFCCFFCC',
			statusColumns: [12, 13, 14, 15]
		},
		{
			match: (row) =>
				ReportExportService.isStatus(row.otStatus, 'Disputed'),
			color: 'FFFFCCCC',
			statusColumns: [12, 13, 14]
		},
		{
			match: (row) =>
				ReportExportService.isStatus(row.otStatus, 'Submitted'),
			color: 'FFFFFFCC',
			statusColumns: [12, 13]
		},
		{
			match: (row) => ReportExportService.isStatus(row.otStatus, 'Saved'),
			color: 'FFCCFFFF',
			statusColumns: [12]
		}
	];

	private static readonly EXPORT_LEGEND_FILLS: [number, number, string][] = [
		[1, 12, 'FFCCFFCC'], // Approved
		[1, 13, 'FFFF7C80'], // Cancelled
		[1, 21, 'FF92D050'], // Approved in Open Invoice
		[1, 22, 'FF92D050'],
		[1, 23, 'FF00B050'], // Paid
		[2, 12, 'FFFFFFCC'], // Submitted
		[2, 21, 'FFFFFF00'], // Submitted in Open Invoice
		[2, 22, 'FFFFFF00'],
		[3, 12, 'FFCCFFFF'], // Saved
		[3, 21, 'FF00B0F0'], // Saved in Open Invoice
		[3, 22, 'FF00B0F0'],
		[4, 12, 'FFFFCCCC'], // Disputed
		[4, 21, 'FFFF0000'], // Disputed in Open Invoice
		[4, 22, 'FFFF0000']
	];

	// Column order and headers match SoPendingBillingResult JSON names.
	private static readonly SO_PENDING_BILLING_COLUMNS: (keyof SoPendingBillingResult)[] =
		[
			'internalId',
			'status',
			'customer',
			'soNumber',
			'poAfe',
			'jobLocation',
			'afeloe',
			'Work Order Number OLD',
			'costCenter',
			'area',
			'category',
			'glNumber',
			'Major.Minor OLD',
			'foreman',
			'orderedBy',
			'soDate',
			'dateCreated',
			'shipDate',
			'qtySold',
			'qtyShipped',
			'qtyUnFF',
			'qtyBilled',
			'qtyUnbilled',
			'unitPrice',
			'woNumber',
			'totalRevenue',
			'amountBilled',
			'amountUnbilled',
			'amountUnFF',
			'item',
			'description',
			'avgItemCost',
			'unFFCost',
			'billedCost',
			'unbilledCost',
			'lpp',
			'open_tasks',
			'has_open_tasks'
		];

	private readonly currencyPipe = inject(CurrencyPipe);
	private readonly datePipe = inject(DatePipe);

	public static isStatus(
		status: string | null | undefined,
		target: string
	): boolean {
		return (status ?? '').trim().toLowerCase() === target.toLowerCase();
	}

	// Single source of truth for "Disputed" membership, shared by the
	// Disputed export tab and the Disputed Report table filter.
	public static isDisputed(row: ReportRow): boolean {
		return (
			ReportExportService.isStatus(
				ReportExportService.effectiveOtStatus(row),
				'Disputed'
			) || ReportExportService.isStatus(row.oiStatus, 'Disputed')
		);
	}

	private static readonly INVOICE_METRICS_RAW_DATA_HEADERS = [
		'IsError',
		'ResponseReceived',
		'WeekStart',
		'Status',
		'TotalAmount',
		'ParentCompany',
		'Destination'
	];

	private static readonly INVOICE_METRICS_SUCCESS_COLOR = '#4CAF50';
	private static readonly INVOICE_METRICS_FAIL_COLOR = '#F44336';

	private static readonly INVOICE_METRICS_COMPANY_COLOR_PALETTE = [
		'#4472C4',
		'#ED7D31',
		'#A5A5A5',
		'#FFC000',
		'#5B9BD5',
		'#70AD47',
		'#264478',
		'#9E480E',
		'#636363',
		'#997300'
	];
	private static readonly INVOICE_METRICS_AMOUNT_COLOR = '#2E7D32';

	private static readonly INVOICE_METRICS_SUCCESS_SAVED_COLOR = '#1F4E78';
	private static readonly INVOICE_METRICS_SUCCESS_SUBMIT_COLOR = '#8FAADC';
	private static readonly INVOICE_METRICS_FAIL_SAVED_COLOR = '#C00000';
	private static readonly INVOICE_METRICS_FAIL_SUBMIT_COLOR = '#FF7C80';

	private static readonly INVOICE_METRICS_COLOR_GUIDE: [string, string][] = [
		['Success - Saved', 'FF1F4E78'],
		['Success - Submit', 'FF8FAADC'],
		['Fail - Saved', 'FFC00000'],
		['Fail - Submit', 'FFFF7C80']
	];

	private static readonly INVOICE_METRICS_HEADER_FONT: Partial<ExcelJS.Font> =
		{
			name: 'Arial',
			size: 11,
			bold: true,
			color: { argb: 'FFFFFFFF' }
		};
	private static readonly INVOICE_METRICS_HEADER_FILL: ExcelJS.Fill = {
		type: 'pattern',
		pattern: 'solid',
		fgColor: { argb: 'FF1F4E78' }
	};
	private static readonly INVOICE_METRICS_TOTAL_FONT: Partial<ExcelJS.Font> =
		{
			name: 'Arial',
			size: 11,
			bold: true
		};

	private static styleInvoiceMetricsHeaderRow(
		row: ExcelJS.Row,
		wrapText = false
	): void {
		row.eachCell((cell) => {
			cell.font = ReportExportService.INVOICE_METRICS_HEADER_FONT;
			cell.fill = ReportExportService.INVOICE_METRICS_HEADER_FILL;
			cell.alignment = { horizontal: 'center', wrapText };
		});
	}

	public async downloadInvoiceMetricsWorkbook(
		logs: InvoiceSubmissionResult[]
	): Promise<void> {
		const workbook = new ExcelJS.Workbook();
		const weeklySummary = ReportExportService.computeWeeklySummary(logs);

		this.addInvoiceMetricsRawDataSheet(workbook, logs);
		this.addInvoiceMetricsWeeklySummarySheet(workbook, weeklySummary);
		this.addInvoiceMetricsChartsSheet(
			workbook,
			weeklySummary,
			'Charts - Week over Week',
			'Week'
		);
		this.addInvoiceMetricsChartsSheet(
			workbook,
			ReportExportService.computeMonthlySummary(logs),
			'Charts - Month over Month',
			'Month'
		);

		const buffer = await workbook.xlsx.writeBuffer();
		const fileDateSuffix =
			this.datePipe.transform(new Date(), 'MMddyyyy') ?? '';
		ReportExportService.downloadBuffer(
			buffer,
			`RK Supply Invoice Metrics_${fileDateSuffix}.xlsx`
		);
	}

	public downloadSoPendingBillingCsv(rows: SoPendingBillingResult[]): void {
		const header = ReportExportService.SO_PENDING_BILLING_COLUMNS.map(
			(column) => ReportExportService.csvField(column)
		).join(',');
		const body = rows.map((row) =>
			ReportExportService.SO_PENDING_BILLING_COLUMNS.map((column) =>
				ReportExportService.csvField(row[column])
			).join(',')
		);
		const csv = [header, ...body].join('\r\n');
		const fileDateSuffix =
			this.datePipe.transform(new Date(), 'MMddyyyy') ?? '';

		const blob = new Blob(['\uFEFF', csv], {
			type: 'text/csv;charset=utf-8'
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `SO Pending Billing_${fileDateSuffix}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}

	private addInvoiceMetricsRawDataSheet(
		workbook: ExcelJS.Workbook,
		logs: InvoiceSubmissionResult[]
	): void {
		const worksheet = workbook.addWorksheet('Raw Data', {
			views: [{ state: 'frozen', ySplit: 1 }]
		});

		const headerRow = worksheet.addRow(
			ReportExportService.INVOICE_METRICS_RAW_DATA_HEADERS
		);
		ReportExportService.styleInvoiceMetricsHeaderRow(headerRow);
		ReportExportService.applyColumnWidths(
			worksheet,
			ReportExportService.INVOICE_METRICS_RAW_DATA_HEADERS
		);

		logs.forEach((log) => {
			const weekStart = ReportExportService.getWeekStart(
				new Date(log.responseReceived)
			);

			worksheet.addRow([
				log.isError,
				this.formatDateTime(log.responseReceived),
				this.datePipe.transform(weekStart, 'yyyy-MM-dd') ?? '',
				log.isError ? 'Error' : 'Success',
				this.currencyPipe.transform(Number(log.totalAmount)) ?? '',
				log.parentCompany ?? '',
				log.destination ?? ''
			]);
		});
	}

	private static readonly INVOICE_METRICS_BAR_CHART_WIDTH = 800;
	private static readonly INVOICE_METRICS_BAR_CHART_HEIGHT = 450;
	private static readonly INVOICE_METRICS_PIE_CHART_WIDTH = 500;
	private static readonly INVOICE_METRICS_PIE_CHART_HEIGHT = 400;
	private static readonly EXCEL_DEFAULT_ROW_HEIGHT_PX = 20;
	private static readonly INVOICE_METRICS_CHARTS_GRID_ROW_GAP = 4;

	private addInvoiceMetricsWeeklySummarySheet(
		workbook: ExcelJS.Workbook,
		weeklySummary: WeeklySummaryRow[]
	): void {
		const worksheet = workbook.addWorksheet('Weekly Summary');

		const headers = [
			'Week Start',
			'Invoices Succeeded',
			'Invoices Failed',
			'Total Submitted',
			'Amount Processed ($)'
		];
		const headerRow = worksheet.addRow(headers);
		ReportExportService.styleInvoiceMetricsHeaderRow(headerRow, true);
		ReportExportService.applyColumnWidths(worksheet, headers);

		weeklySummary.forEach((week) => {
			const row = worksheet.addRow([
				this.datePipe.transform(week.weekStart, 'M/d/yyyy') ?? '',
				week.succeeded,
				week.failed,
				week.total,
				week.amountProcessed
			]);
			row.getCell(5).numFmt = '$#,##0.00';
		});

		const totalSucceeded = weeklySummary.reduce(
			(sum, week) => sum + week.succeeded,
			0
		);
		const totalFailed = weeklySummary.reduce(
			(sum, week) => sum + week.failed,
			0
		);
		const totalSubmitted = totalSucceeded + totalFailed;
		const totalAmountProcessed = weeklySummary.reduce(
			(sum, week) => sum + week.amountProcessed,
			0
		);

		const totalRow = worksheet.addRow([
			'Total',
			totalSucceeded,
			totalFailed,
			totalSubmitted,
			totalAmountProcessed
		]);
		totalRow.eachCell((cell) => {
			cell.font = ReportExportService.INVOICE_METRICS_TOTAL_FONT;
		});
		totalRow.getCell(5).numFmt = '$#,##0.00';

		const rateRow = worksheet.addRow([
			'Rate',
			totalSubmitted ? totalSucceeded / totalSubmitted : 0,
			totalSubmitted ? totalFailed / totalSubmitted : 0,
			'',
			''
		]);
		rateRow.getCell(2).numFmt = '0.0%';
		rateRow.getCell(3).numFmt = '0.0%';
	}

	// The 6 charts from the sample "Permian Resources"-style workbook's Charts
	// tab, laid out in a single vertical column. Chart.js has no "grouped +
	// stacked" bar type, so the by-destination breakdown approximates the
	// sample's two-level week/status category axis with flat "<week> Success"
	// / "<week> Failed" labels instead.
	private addInvoiceMetricsChartsSheet(
		workbook: ExcelJS.Workbook,
		weeklySummary: WeeklySummaryRow[],
		sheetName: string,
		period: 'Week' | 'Month'
	): void {
		const worksheet = workbook.addWorksheet(sheetName, {
			views: [{ showGridLines: false }]
		});

		const weekLabels = weeklySummary.map(
			(week) =>
				this.datePipe.transform(
					week.weekStart,
					period === 'Week' ? 'M/d' : 'MMM yyyy'
				) ?? ''
		);
		const formatCurrency = (value: number): string =>
			this.currencyPipe.transform(value) ?? '';
		const formatCount = (value: number): string => value.toLocaleString();

		const totalSucceeded = weeklySummary.reduce(
			(sum, week) => sum + week.succeeded,
			0
		);
		const totalFailed = weeklySummary.reduce(
			(sum, week) => sum + week.failed,
			0
		);

		const statusChartImage = ReportExportService.renderChartImage(
			{
				type: 'bar',
				plugins: [
					ReportExportService.createBarValueLabelPlugin(
						formatCount,
						'top'
					)
				],
				data: {
					labels: weekLabels,
					datasets: [
						{
							label: 'Invoices Succeeded',
							data: weeklySummary.map((week) => week.succeeded),
							backgroundColor:
								ReportExportService.INVOICE_METRICS_SUCCESS_COLOR
						},
						{
							label: 'Invoices Failed',
							data: weeklySummary.map((week) => week.failed),
							backgroundColor:
								ReportExportService.INVOICE_METRICS_FAIL_COLOR
						}
					]
				},
				options: {
					plugins: {
						title: {
							display: true,
							text: `Invoices Succeeded vs Failed — ${period} over ${period}`
						}
					},
					scales: {
						x: { title: { display: true, text: `${period} Start` } },
						y: {
							title: { display: true, text: 'Invoice Count' },
							beginAtZero: true
						}
					}
				}
			},
			ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
			ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
		);

		const pieChartImage = ReportExportService.renderChartImage(
			{
				type: 'pie',
				plugins: [ReportExportService.createPieValueLabelPlugin()],
				data: {
					labels: ['Succeeded', 'Failed'],
					datasets: [
						{
							data: [totalSucceeded, totalFailed],
							backgroundColor: [
								ReportExportService.INVOICE_METRICS_SUCCESS_COLOR,
								ReportExportService.INVOICE_METRICS_FAIL_COLOR
							]
						}
					]
				},
				options: {
					plugins: {
						title: {
							display: true,
							text: 'Invoice Submission Success Rate'
						}
					}
				}
			},
			ReportExportService.INVOICE_METRICS_PIE_CHART_WIDTH,
			ReportExportService.INVOICE_METRICS_PIE_CHART_HEIGHT
		);

		const amountChartImage = ReportExportService.renderChartImage(
			{
				type: 'bar',
				plugins: [
					ReportExportService.createBarValueLabelPlugin(
						formatCurrency,
						'top'
					)
				],
				data: {
					labels: weekLabels,
					datasets: [
						{
							label: 'Amount Processed ($)',
							data: weeklySummary.map(
								(week) => week.amountProcessed
							),
							backgroundColor:
								ReportExportService.INVOICE_METRICS_AMOUNT_COLOR
						}
					]
				},
				options: {
					plugins: {
						title: {
							display: true,
							text: `Amount Processed ($) — ${period} over ${period}`
						},
						legend: { display: false }
					},
					scales: {
						x: { title: { display: true, text: `${period} Start` } },
						y: {
							title: {
								display: true,
								text: 'Amount Processed ($)'
							},
							beginAtZero: true
						}
					}
				}
			},
			ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
			ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
		);

		const allCompanies = Array.from(
			new Set(
				weeklySummary.flatMap((week) => Object.keys(week.companyCounts))
			)
		).sort();
		const companyColorPalette =
			ReportExportService.INVOICE_METRICS_COMPANY_COLOR_PALETTE;

		const companyChartImage = ReportExportService.renderChartImage(
			{
				type: 'bar',
				plugins: [
					ReportExportService.createBarValueLabelPlugin(
						formatCount,
						'center'
					)
				],
				data: {
					labels: weekLabels,
					datasets: allCompanies.map((company, index) => ({
						label: company,
						data: weeklySummary.map(
							(week) => week.companyCounts[company] ?? 0
						),
						backgroundColor:
							companyColorPalette[
								index % companyColorPalette.length
							],
						stack: 'company'
					}))
				},
				options: {
					plugins: {
						title: {
							display: true,
							text: `Invoices Submitted per Parent Company — ${period} over ${period}`
						}
					},
					scales: {
						x: {
							stacked: true,
							title: { display: true, text: `${period} Start` }
						},
						y: {
							stacked: true,
							title: { display: true, text: 'Invoice Count' },
							beginAtZero: true
						}
					}
				}
			},
			ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
			ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
		);

		const destinationChartImage = ReportExportService.renderChartImage(
			{
				type: 'bar',
				plugins: [
					ReportExportService.createBarValueLabelPlugin(
						formatCount,
						'center'
					)
				],
				data: {
					labels: weekLabels,
					datasets: [
						{
							label: 'saved',
							data: weeklySummary.map(
								(week) => week.destinationTotals.saved
							),
							backgroundColor:
								ReportExportService.INVOICE_METRICS_SUCCESS_SAVED_COLOR,
							stack: 'destination'
						},
						{
							label: 'submit',
							data: weeklySummary.map(
								(week) => week.destinationTotals.submit
							),
							backgroundColor:
								ReportExportService.INVOICE_METRICS_SUCCESS_SUBMIT_COLOR,
							stack: 'destination'
						}
					]
				},
				options: {
					plugins: {
						title: {
							display: true,
							text: `Total Invoices by Destination — ${period} over ${period}`
						}
					},
					scales: {
						x: {
							stacked: true,
							title: { display: true, text: `${period} Start` }
						},
						y: {
							stacked: true,
							title: { display: true, text: 'Invoice Count' },
							beginAtZero: true
						}
					}
				}
			},
			ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
			ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
		);

		const statusDestinationLabels = weekLabels.flatMap((label) => [
			`${label} Success`,
			`${label} Failed`
		]);

		const statusDestinationChartImage =
			ReportExportService.renderChartImage(
				{
					type: 'bar',
					plugins: [
						ReportExportService.createBarValueLabelPlugin(
							formatCount,
							'center'
						)
					],
					data: {
						labels: statusDestinationLabels,
						datasets: [
							{
								label: 'saved',
								data: weeklySummary.flatMap((week) => [
									week.successByDestination.saved,
									week.failedByDestination.saved
								]),
								backgroundColor: weeklySummary.flatMap(() => [
									ReportExportService.INVOICE_METRICS_SUCCESS_SAVED_COLOR,
									ReportExportService.INVOICE_METRICS_FAIL_SAVED_COLOR
								]),
								stack: 'destination'
							},
							{
								label: 'submit',
								data: weeklySummary.flatMap((week) => [
									week.successByDestination.submit,
									week.failedByDestination.submit
								]),
								backgroundColor: weeklySummary.flatMap(() => [
									ReportExportService.INVOICE_METRICS_SUCCESS_SUBMIT_COLOR,
									ReportExportService.INVOICE_METRICS_FAIL_SUBMIT_COLOR
								]),
								stack: 'destination'
							}
						]
					},
					options: {
						plugins: {
							title: {
								display: true,
								text: `Invoices Submitted vs Failed — ${period} over ${period} (by Destination)`
							}
						},
						scales: {
							x: {
								stacked: true,
								title: { display: true, text: `${period} / Status` },
								ticks: { autoSkip: false }
							},
							y: {
								stacked: true,
								title: { display: true, text: 'Invoice Count' },
								beginAtZero: true
							}
						}
					}
				},
				ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
				ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
			);

		const charts = [
			{
				image: statusChartImage,
				width: ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
				height: ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
			},
			{
				image: pieChartImage,
				width: ReportExportService.INVOICE_METRICS_PIE_CHART_WIDTH,
				height: ReportExportService.INVOICE_METRICS_PIE_CHART_HEIGHT
			},
			{
				image: amountChartImage,
				width: ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
				height: ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
			},
			{
				image: companyChartImage,
				width: ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
				height: ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
			},
			{
				image: destinationChartImage,
				width: ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
				height: ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
			},
			{
				image: statusDestinationChartImage,
				width: ReportExportService.INVOICE_METRICS_BAR_CHART_WIDTH,
				height: ReportExportService.INVOICE_METRICS_BAR_CHART_HEIGHT
			}
		];

		let currentRow = 0;
		let statusDestinationChartRow = 0;
		charts.forEach((chart, index) => {
			if (index === charts.length - 1) {
				statusDestinationChartRow = currentRow;
			}
			ReportExportService.placeChartImage(
				workbook,
				worksheet,
				chart.image,
				0,
				currentRow,
				chart.width,
				chart.height
			);
			const rowSpan = Math.ceil(
				chart.height / ReportExportService.EXCEL_DEFAULT_ROW_HEIGHT_PX
			);
			currentRow +=
				rowSpan +
				ReportExportService.INVOICE_METRICS_CHARTS_GRID_ROW_GAP;
		});

		ReportExportService.addColorGuideLegend(
			worksheet,
			statusDestinationChartRow + 1
		);
	}

	private static readonly INVOICE_METRICS_LEGEND_SWATCH_COLUMN = 15;

	private static addColorGuideLegend(
		worksheet: ExcelJS.Worksheet,
		startRow: number
	): void {
		const swatchColumn =
			ReportExportService.INVOICE_METRICS_LEGEND_SWATCH_COLUMN;
		const labelColumn = swatchColumn + 1;
		worksheet.getColumn(swatchColumn).width = 4;

		const titleCell = worksheet.getRow(startRow).getCell(labelColumn);
		titleCell.value = 'Color Guide';
		titleCell.font = { name: 'Arial', size: 11, bold: true };

		ReportExportService.INVOICE_METRICS_COLOR_GUIDE.forEach(
			([label, argb], index) => {
				const rowNumber = startRow + index + 1;
				const swatchCell = worksheet
					.getRow(rowNumber)
					.getCell(swatchColumn);
				swatchCell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb }
				};

				const labelCell = worksheet
					.getRow(rowNumber)
					.getCell(labelColumn);
				labelCell.value = label;
				labelCell.font = { name: 'Arial', size: 10 };
			}
		);
	}

	private static placeChartImage(
		workbook: ExcelJS.Workbook,
		worksheet: ExcelJS.Worksheet,
		base64: string,
		col: number,
		row: number,
		width: number,
		height: number
	): void {
		const imageId = workbook.addImage({ base64, extension: 'png' });
		worksheet.addImage(imageId, {
			tl: { col, row },
			ext: { width, height }
		});
	}

	private formatDateTime(value: string): string {
		return this.datePipe.transform(value, 'yyyy-MM-dd HH:mm:ss') ?? '';
	}

	private static getWeekStart(date: Date): Date {
		const monday = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);
		const day = monday.getDay();
		monday.setDate(monday.getDate() + (day === 0 ? -6 : 1 - day));
		return monday;
	}

	private static getMonthStart(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth(), 1);
	}

	private static computeWeeklySummary(
		logs: InvoiceSubmissionResult[]
	): WeeklySummaryRow[] {
		return ReportExportService.computePeriodSummary(
			logs,
			(date) => ReportExportService.getWeekStart(date)
		);
	}

	private static computeMonthlySummary(
		logs: InvoiceSubmissionResult[]
	): WeeklySummaryRow[] {
		return ReportExportService.computePeriodSummary(
			logs,
			(date) => ReportExportService.getMonthStart(date)
		);
	}

	private static computePeriodSummary(
		logs: InvoiceSubmissionResult[],
		getPeriodStart: (date: Date) => Date
	): WeeklySummaryRow[] {
		const weekMap = new Map<number, WeeklySummaryRow>();

		logs.forEach((log) => {
			const weekStart = getPeriodStart(new Date(log.responseReceived));
			const key = weekStart.getTime();
			const week = weekMap.get(key) ?? {
				weekStart,
				succeeded: 0,
				failed: 0,
				total: 0,
				amountProcessed: 0,
				companyCounts: {},
				destinationTotals: { saved: 0, submit: 0 },
				successByDestination: { saved: 0, submit: 0 },
				failedByDestination: { saved: 0, submit: 0 }
			};

			if (log.isError) week.failed++;
			else week.succeeded++;
			week.total++;

			if (log.destination === 'saved') week.destinationTotals.saved++;
			else if (log.destination === 'submit')
				week.destinationTotals.submit++;

			if (log.isError) {
				if (log.destination === 'saved')
					week.failedByDestination.saved++;
				else if (log.destination === 'submit')
					week.failedByDestination.submit++;
			} else {
				week.amountProcessed += Number(log.totalAmount) || 0;
				if (log.parentCompany) {
					week.companyCounts[log.parentCompany] =
						(week.companyCounts[log.parentCompany] ?? 0) + 1;
				}
				if (log.destination === 'saved')
					week.successByDestination.saved++;
				else if (log.destination === 'submit')
					week.successByDestination.submit++;
			}

			weekMap.set(key, week);
		});

		return Array.from(weekMap.values()).sort(
			(a, b) => a.weekStart.getTime() - b.weekStart.getTime()
		);
	}

	private static createBarValueLabelPlugin(
		formatValue: (value: number) => string,
		labelPosition: 'top' | 'center'
	): Plugin<'bar'> {
		return {
			id: 'barValueLabels',
			afterDatasetsDraw(chart) {
				const { ctx } = chart;
				chart.data.datasets.forEach((dataset, datasetIndex) => {
					const meta = chart.getDatasetMeta(datasetIndex);
					meta.data.forEach((element, index) => {
						const value = Number(dataset.data[index] ?? 0);
						if (!value) return;

						const { x, y, base } = (element as BarElement).getProps(
							['x', 'y', 'base'],
							true
						);
						if (x === null || y === null) return;
						const labelY =
							labelPosition === 'top' ? y - 6 : (y + base) / 2;

						ctx.save();
						ctx.font = 'bold 11px Arial';
						ctx.textAlign = 'center';
						ctx.textBaseline =
							labelPosition === 'top' ? 'bottom' : 'middle';
						if (labelPosition === 'center') {
							// Small stacked segments push this label past their
							// own edges; the outline keeps it legible against
							// whatever ends up behind it instead of blending
							// into the white chart background.
							ctx.lineWidth = 3;
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
							ctx.strokeText(formatValue(value), x, labelY);
							ctx.fillStyle = '#FFFFFF';
						} else {
							ctx.fillStyle = '#333333';
						}
						ctx.fillText(formatValue(value), x, labelY);
						ctx.restore();
					});
				});
			}
		};
	}

	private static createPieValueLabelPlugin(): Plugin<'pie'> {
		return {
			id: 'pieValueLabels',
			afterDatasetsDraw(chart) {
				const { ctx } = chart;
				const dataset = chart.data.datasets[0];
				if (!dataset) return;

				const total = dataset.data.reduce(
					(sum, value) => sum + value,
					0
				);
				if (!total) return;

				const meta = chart.getDatasetMeta(0);
				meta.data.forEach((element, index) => {
					const value = dataset.data[index] ?? 0;
					if (!value) return;

					const {
						x,
						y,
						startAngle,
						endAngle,
						innerRadius,
						outerRadius
					} = (element as ArcElement).getProps(
						[
							'x',
							'y',
							'startAngle',
							'endAngle',
							'innerRadius',
							'outerRadius'
						],
						true
					);
					if (x === null || y === null) return;
					const midAngle = (startAngle + endAngle) / 2;
					const radius = (innerRadius + outerRadius) / 2;
					const labelX = x + radius * Math.cos(midAngle);
					const labelY = y + radius * Math.sin(midAngle);
					const percentage = `${((value / total) * 100).toFixed(1)}%`;

					ctx.save();
					ctx.font = 'bold 12px Arial';
					ctx.textAlign = 'center';
					ctx.textBaseline = 'middle';
					ctx.fillStyle = '#FFFFFF';
					ctx.fillText(percentage, labelX, labelY);
					ctx.restore();
				});
			}
		};
	}

	// Renders a Chart.js chart to a detached canvas and returns it as a
	// base64-encoded PNG for embedding via ExcelJS, which has no native chart
	// support of its own.
	private static renderChartImage(
		config: ChartConfiguration,
		width: number,
		height: number
	): string {
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;

		const chart = new Chart(canvas, {
			...config,
			options: { ...config.options, responsive: false, animation: false }
		});
		const base64 = canvas.toDataURL('image/png').split(',')[1] ?? '';
		chart.destroy();

		return base64;
	}

	public async downloadWorkbook(
		rows: ReportRow[],
		viewLabel?: string,
		disputedOnly?: boolean
	): Promise<void> {
		const workbook = this.buildExportWorkbook(rows, disputedOnly);
		const buffer = await workbook.xlsx.writeBuffer();
		const fileDateSuffix =
			this.datePipe.transform(new Date(), 'MMddyyyy') ?? '';
		const fileNameParts = [
			'RK Supply Reporting',
			viewLabel,
			fileDateSuffix
		].filter(Boolean);
		ReportExportService.downloadBuffer(
			buffer,
			`${fileNameParts.join('_')}.xlsx`
		);
	}

	public buildExportWorkbook(
		rows: ReportRow[],
		disputedOnly?: boolean
	): ExcelJS.Workbook {
		const workbook = new ExcelJS.Workbook();

		if (disputedOnly) {
			const disputedTab = ReportExportService.EXPORT_STATUS_TABS.find(
				(tab) => tab.name === 'Disputed'
			);
			this.addReportSheet(workbook, 'Disputed', rows, disputedTab?.color);
			return workbook;
		}

		const resolvedStatusTabs = ReportExportService.EXPORT_STATUS_TABS.map(
			(tab) => ({
				name: tab.name,
				color: tab.color,
				rows: rows.filter(tab.filter)
			})
		);

		this.addSummarySheet(workbook, rows);

		const snapshotSheetName =
			this.datePipe.transform(new Date(), 'M-d-yyyy') ?? 'Report';
		this.addReportSheet(workbook, snapshotSheetName, rows);

		resolvedStatusTabs.forEach((tab) => {
			this.addReportSheet(workbook, tab.name, tab.rows, tab.color);
		});

		return workbook;
	}

	private addReportSheet(
		workbook: ExcelJS.Workbook,
		name: string,
		rows: ReportRow[],
		tabColorArgb?: string
	): void {
		const worksheet = workbook.addWorksheet(
			name,
			tabColorArgb
				? { properties: { tabColor: { argb: tabColorArgb } } }
				: {}
		);

		const asOfDate = this.datePipe.transform(new Date(), 'MMMM d, y') ?? '';
		const columnCount = ReportExportService.EXPORT_FIELD_LAYOUT.length;
		const blankRow = (): string[] =>
			Array.from({ length: columnCount }, () => '');

		const row1 = blankRow();
		row1[0] = 'RK Supply';
		row1[11] = 'Approved';
		row1[12] = 'Cancelled';
		row1[20] = 'Approved in Open Invoice';
		row1[22] = 'Paid';

		const row2 = blankRow();
		row2[0] = 'Parent Company';
		row2[11] = 'Submitted';
		row2[20] = 'Submitted in Open Invoice';
		row2[22] = 'PD - Open Amt';

		const row3 = blankRow();
		row3[0] = 'A/R_with_Company_Man_Sales Order';
		row3[11] = 'Saved';
		row3[20] = 'Saved in Open Invoice';

		const row4 = blankRow();
		row4[0] = `As of ${asOfDate}`;
		row4[11] = 'Disputed';
		row4[20] = 'Disputed in Open Invoice';

		const row5 = blankRow();
		row5[11] = 'Open Ticket';
		row5[20] = 'Open Invoice';
		row5[26] = 'Number of Days Between';

		worksheet.addRow(row1);
		worksheet.addRow(row2);
		worksheet.addRow(row3);
		worksheet.addRow(row4);
		worksheet.addRow(row5);
		ReportExportService.styleReportHeaderBlock(worksheet);

		const headerValues = this.buildExportHeaderRow();
		const headerRow = worksheet.addRow(headerValues);
		ReportExportService.fillCells(
			headerRow,
			ReportExportService.EXPORT_COLUMN_HEADER_FILL,
			Array.from({ length: columnCount }, (_, index) => index + 1)
		);
		ReportExportService.applyColumnWidths(worksheet, headerValues);
		worksheet.autoFilter = {
			from: { row: headerRow.number, column: 1 },
			to: { row: headerRow.number, column: columnCount }
		};

		rows.forEach((row) => {
			const sheetRow = worksheet.addRow(this.buildExportDataRow(row));
			sheetRow.font = ReportExportService.EXPORT_DATA_FONT;

			ReportExportService.EXPORT_DAY_COUNT_COLUMNS.forEach((column) => {
				const dayCountCell = sheetRow.getCell(column);
				dayCountCell.numFmt =
					ReportExportService.EXPORT_DAY_COUNT_NUMBER_FORMAT;
				dayCountCell.alignment =
					ReportExportService.EXPORT_CENTER_ALIGNMENT;
			});
			ReportExportService.EXPORT_DATE_COLUMNS.forEach((column) => {
				sheetRow.getCell(column).numFmt =
					ReportExportService.EXPORT_DATE_NUMBER_FORMAT;
			});
			sheetRow.getCell(
				ReportExportService.EXPORT_OPEN_BALANCE_COLUMN
			).numFmt = ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT;
			sheetRow.getCell(ReportExportService.EXPORT_AGE_COLUMN).alignment =
				ReportExportService.EXPORT_CENTER_ALIGNMENT;

			const rowFill = ReportExportService.EXPORT_ROW_FILLS.find(
				(candidate) => candidate.match(row)
			);
			// Rows with neither an OT nor an OI status are left unfilled, as in
			// the source workbook.
			if (!rowFill) return;

			ReportExportService.fillCells(sheetRow, rowFill.color, [
				...ReportExportService.EXPORT_INVOICE_BLOCK_COLUMNS,
				...rowFill.statusColumns
			]);
		});

		this.addOpenBalanceTotalRow(worksheet, rows, columnCount);

		ReportExportService.EXPORT_HEADER_MERGES.forEach(
			([top, left, bottom, right]) => {
				worksheet.mergeCells(top, left, bottom, right);
			}
		);

		// After the merges, so each fill lands on the resolved master cell.
		ReportExportService.EXPORT_LEGEND_FILLS.forEach(
			([rowNumber, column, argb]) => {
				ReportExportService.fillCells(
					worksheet.getRow(rowNumber),
					argb,
					[column]
				);
				const legendCell = worksheet.getRow(rowNumber).getCell(column);
				legendCell.font = ReportExportService.EXPORT_LEGEND_FONT;
				// Only the OI legend block (U:W) is centred in the source
				// workbook; the OT legend block (L:M) uses general alignment.
				if (column >= 21 && column <= 23) {
					legendCell.alignment =
						ReportExportService.EXPORT_CENTER_ALIGNMENT;
				}
			}
		);
	}

	// Styles cells A1-A4, L5, U5 and AA5 of the header block to match the
	// source workbook: bold Arial titles/section labels, centred.
	private static styleReportHeaderBlock(worksheet: ExcelJS.Worksheet): void {
		[1, 2].forEach((rowNumber) => {
			const cell = worksheet.getRow(rowNumber).getCell(1);
			cell.font = ReportExportService.EXPORT_TITLE_FONT;
			cell.alignment = ReportExportService.EXPORT_CENTER_ALIGNMENT;
		});

		[3, 4].forEach((rowNumber) => {
			const cell = worksheet.getRow(rowNumber).getCell(1);
			cell.font = ReportExportService.EXPORT_SECTION_FONT;
			cell.alignment = ReportExportService.EXPORT_CENTER_ALIGNMENT;
		});

		const row5 = worksheet.getRow(5);

		const openTicketCell = row5.getCell(12);
		openTicketCell.font = ReportExportService.EXPORT_SECTION_FONT;
		openTicketCell.alignment = ReportExportService.EXPORT_CENTER_ALIGNMENT;
		openTicketCell.border = ReportExportService.EXPORT_SECTION_BORDER;

		const openInvoiceCell = row5.getCell(21);
		openInvoiceCell.font = ReportExportService.EXPORT_SECTION_FONT;
		openInvoiceCell.alignment =
			ReportExportService.EXPORT_CENTER_WRAP_ALIGNMENT;
		openInvoiceCell.border = ReportExportService.EXPORT_SECTION_BORDER;

		const daysBetweenCell = row5.getCell(27);
		daysBetweenCell.font = ReportExportService.EXPORT_DAYS_HEADER_FONT;
		daysBetweenCell.alignment =
			ReportExportService.EXPORT_CENTER_WRAP_ALIGNMENT;
		daysBetweenCell.border = ReportExportService.EXPORT_SECTION_BORDER;
	}

	// Every report sheet (snapshot and status tabs alike) totals Open Balance
	// (column J) at the bottom, highlighted yellow with red text — copied
	// from the source workbook.
	private addOpenBalanceTotalRow(
		worksheet: ExcelJS.Worksheet,
		rows: ReportRow[],
		columnCount: number
	): void {
		const total = rows.reduce((sum, row) => sum + row.amount, 0);
		const totalRow = worksheet.addRow(
			Array.from({ length: columnCount }, () => '')
		);
		totalRow.getCell(ReportExportService.EXPORT_OPEN_BALANCE_COLUMN).value =
			this.currencyPipe.transform(total) ?? '$0';

		ReportExportService.fillCells(
			totalRow,
			ReportExportService.EXPORT_COLUMN_TOTAL_FILL,
			[ReportExportService.EXPORT_OPEN_BALANCE_COLUMN],
			ReportExportService.EXPORT_COLUMN_TOTAL_FONT
		);
	}

	private static applyColumnWidths(
		worksheet: ExcelJS.Worksheet,
		headerValues: string[]
	): void {
		headerValues.forEach((header, index) => {
			worksheet.getColumn(index + 1).width = Math.max(
				header.length + ReportExportService.EXPORT_COLUMN_WIDTH_PADDING,
				ReportExportService.EXPORT_COLUMN_MIN_WIDTH
			);
		});
	}

	private static fillCells(
		row: ExcelJS.Row,
		argb: string,
		columns: number[],
		font?: Partial<ExcelJS.Font>
	): void {
		columns.forEach((column) => {
			const cell = row.getCell(column);
			cell.fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb }
			};
			if (font) cell.font = { ...cell.font, ...font };
		});
	}

	private static addSummaryBanner(
		worksheet: ExcelJS.Worksheet,
		text: string
	): void {
		const row = worksheet.addRow([`  ${text}`]);
		worksheet.mergeCells(row.number, 1, row.number, 6);
		row.font = ReportExportService.SUMMARY_BANNER_FONT;
		ReportExportService.fillCells(
			row,
			ReportExportService.SUMMARY_BANNER_FILL,
			[1, 2, 3, 4, 5, 6]
		);
	}

	private static addSummaryHeaderRow(
		worksheet: ExcelJS.Worksheet,
		headers: string[]
	): void {
		const row = worksheet.addRow(headers);
		row.font = ReportExportService.SUMMARY_HEADER_FONT;
		ReportExportService.fillCells(
			row,
			ReportExportService.SUMMARY_HEADER_FILL,
			[1, 2, 3, 4, 5, 6]
		);
	}

	private static addSummaryFootnote(
		worksheet: ExcelJS.Worksheet,
		text: string
	): void {
		const row = worksheet.addRow([text]);
		worksheet.mergeCells(row.number, 1, row.number, 6);
		row.font = ReportExportService.SUMMARY_FOOTNOTE_FONT;
	}

	private static addSummaryKeyFigureRow(
		worksheet: ExcelJS.Worksheet,
		label: string,
		value: number | undefined,
		numFmt?: string
	): void {
		const row = worksheet.addRow([label, value ?? '']);
		worksheet.mergeCells(row.number, 2, row.number, 3);
		row.font = ReportExportService.SUMMARY_DATA_FONT;
		if (numFmt && value !== undefined) row.getCell(2).numFmt = numFmt;
	}

	private addSummarySheet(
		workbook: ExcelJS.Workbook,
		rows: ReportRow[]
	): void {
		const worksheet = workbook.addWorksheet('Summary', {
			properties: { tabColor: { argb: 'FFC00000' } }
		});
		worksheet.columns = ReportExportService.SUMMARY_COLUMN_WIDTHS.map(
			(width) => ({ width })
		);

		const asOfDate = this.datePipe.transform(new Date(), 'MMMM d, y') ?? '';
		const companyTitle = ReportExportService.resolveCompanyTitle(rows);

		worksheet.addRow(['RK SUPPLY']).getCell(1).font =
			ReportExportService.SUMMARY_MASTHEAD_FONT;
		worksheet.addRow([companyTitle]).getCell(1).font =
			ReportExportService.SUMMARY_TITLE_FONT;
		worksheet
			.addRow(['Accounts Receivable – Status Summary'])
			.getCell(1).font = ReportExportService.SUMMARY_SUBTITLE_FONT;
		worksheet.addRow([`As of ${asOfDate}`]).getCell(1).font =
			ReportExportService.SUMMARY_ASOF_FONT;
		worksheet.addRow([]);

		// --- Status Breakdown ---------------------------------------------
		ReportExportService.addSummaryBanner(worksheet, 'STATUS BREAKDOWN');
		ReportExportService.addSummaryHeaderRow(worksheet, [
			'Status',
			'Invoice Count',
			'% of Total',
			'Avg. Days in Status',
			'Basis',
			'Total Open Balance'
		]);

		const totalCount = rows.length;
		const totalBalance = rows.reduce((sum, row) => sum + row.amount, 0);

		ReportExportService.EXPORT_STATUS_TABS.forEach((tab, index) => {
			const tabRows = rows.filter(tab.filter);
			const count = tabRows.length;
			const openBalance = tabRows.reduce(
				(sum, row) => sum + row.amount,
				0
			);
			const avgDays = ReportExportService.average(
				tabRows.map(tab.summaryAvgDays)
			);

			const row = worksheet.addRow([
				tab.summaryLabel,
				count,
				totalCount ? count / totalCount : 0,
				avgDays ?? '',
				tab.summaryBasis,
				openBalance
			]);
			row.font = ReportExportService.SUMMARY_DATA_FONT;
			row.getCell(3).numFmt = '0.0%';
			row.getCell(4).numFmt = '0.0';
			row.getCell(5).font = ReportExportService.SUMMARY_BASIS_FONT;
			row.getCell(6).numFmt =
				ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT;

			ReportExportService.fillCells(row, tab.summaryColor, [1]);
			if (index % 2 === 1) {
				ReportExportService.fillCells(
					row,
					ReportExportService.SUMMARY_BAND_FILL,
					[2, 3, 4, 5, 6]
				);
			}
		});

		const totalRow = worksheet.addRow([
			'All Open Line Items',
			totalCount,
			totalCount ? 1 : 0,
			'',
			'',
			totalBalance
		]);
		totalRow.font = ReportExportService.SUMMARY_BOLD_FONT;
		totalRow.getCell(3).numFmt = '0.0%';
		totalRow.getCell(6).numFmt =
			ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT;

		ReportExportService.addSummaryFootnote(
			worksheet,
			'Note: a line item can appear on more than one status view (e.g. ticket approved but invoice disputed), so status counts above can exceed "All Open Line Items."'
		);
		worksheet.addRow([]);

		// --- Key Figures ----------------------------------------------------
		ReportExportService.addSummaryBanner(worksheet, 'KEY FIGURES');

		const averageAge = ReportExportService.average(
			rows.map((row) => ReportExportService.parseDays(row.age))
		);
		const averageSubmittedToPaid = ReportExportService.average(
			rows.map((row) =>
				ReportExportService.parseDays(row.oiSubToPaidDays)
			)
		);
		const disputedBalance = rows
			.filter((row) => ReportExportService.isDisputed(row))
			.reduce((sum, row) => sum + row.amount, 0);

		ReportExportService.addSummaryKeyFigureRow(
			worksheet,
			'Total Open Line Items',
			totalCount
		);
		ReportExportService.addSummaryKeyFigureRow(
			worksheet,
			'Total Open Balance',
			totalBalance,
			ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT
		);
		ReportExportService.addSummaryKeyFigureRow(
			worksheet,
			'Average Invoice Age (days)',
			averageAge,
			'0.0'
		);
		ReportExportService.addSummaryKeyFigureRow(
			worksheet,
			'Avg. Days: Invoice Submitted → Paid',
			averageSubmittedToPaid,
			'0.0'
		);
		ReportExportService.addSummaryKeyFigureRow(
			worksheet,
			'Disputed Balance',
			disputedBalance,
			ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT
		);
		worksheet.addRow([]);

		// --- Requisitioner Ticket Volume -------------------------------------
		ReportExportService.addSummaryBanner(
			worksheet,
			'REQUISITIONER TICKET VOLUME'
		);
		ReportExportService.addSummaryHeaderRow(worksheet, [
			'Requisitioner',
			'Total Tickets',
			'Approved',
			'Submitted',
			'Disputed',
			'Open Balance'
		]);

		const requisitioners =
			ReportExportService.buildRequisitionerSummary(rows);
		requisitioners.slice(0, 10).forEach((entry, index) => {
			const row = worksheet.addRow([
				entry.requisitioner,
				entry.totalTickets,
				entry.approved,
				entry.submitted,
				entry.disputed,
				entry.openBalance
			]);
			row.font = ReportExportService.SUMMARY_DATA_FONT;
			row.getCell(6).numFmt =
				ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT;
			if (index % 2 === 1) {
				ReportExportService.fillCells(
					row,
					ReportExportService.SUMMARY_BAND_FILL,
					[1, 2, 3, 4, 5, 6]
				);
			}
		});

		ReportExportService.addSummaryFootnote(
			worksheet,
			`Showing top 10 of ${requisitioners.length} requisitioners with open tickets, ranked by total ticket volume.`
		);
		worksheet.addRow([]);

		// --- Top 10 Invoices Needing Attention --------------------------------
		ReportExportService.addSummaryBanner(
			worksheet,
			'TOP 10 INVOICES NEEDING ATTENTION'
		);
		ReportExportService.addSummaryHeaderRow(worksheet, [
			'Customer / Job',
			'Document #',
			'Status',
			'Days Outstanding',
			'Open Balance',
			'Requisitioner'
		]);

		ReportExportService.buildInvoicesNeedingAttention(rows).forEach(
			(entry, index) => {
				const row = worksheet.addRow([
					entry.customerJobLocation,
					entry.documentNumber,
					entry.status,
					entry.daysOutstanding,
					entry.openBalance,
					entry.requisitioner
				]);
				row.font = ReportExportService.SUMMARY_DATA_FONT;
				row.getCell(5).numFmt =
					ReportExportService.EXPORT_CURRENCY_NUMBER_FORMAT;
				if (index % 2 === 1) {
					ReportExportService.fillCells(
						row,
						ReportExportService.SUMMARY_BAND_FILL,
						[1, 2, 3, 4, 5, 6]
					);
				}
			}
		);

		ReportExportService.addSummaryFootnote(
			worksheet,
			'Ranked by status severity (Disputed, then Submitted, then Saved/Not Submitted; Approved, Paid, and Cancelled items are excluded as already resolved), then by days outstanding within that severity.'
		);
	}

	private static csvField(
		value: string | number | boolean | null | undefined
	): string {
		const text = String(value ?? '');
		return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
	}

	private static downloadBuffer(
		buffer: ExcelJS.Buffer,
		fileName: string
	): void {
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}

	private buildExportHeaderRow(): string[] {
		return ReportExportService.EXPORT_FIELD_LAYOUT.map((field) => {
			if (!field) return '';
			return (
				ReportExportService.REPORT_COLUMNS.find(
					(c) => c.field === field
				)?.header ?? ''
			);
		});
	}

	private buildExportDataRow(row: ReportRow): (string | number | Date)[] {
		const effectiveOtStatus =
			ReportExportService.effectiveOtStatus(row) ?? '';
		const effectiveOiStatus =
			ReportExportService.effectiveOiStatus(row) ?? '';

		return ReportExportService.EXPORT_FIELD_LAYOUT.map((field) => {
			if (!field) return '';
			if (field === 'otStatus') return effectiveOtStatus;
			if (field === 'oiStatus') return effectiveOiStatus;

			// Open Balance is stored as a pre-formatted currency string for the
			// on-screen table; the export uses the underlying number instead so
			// Excel can sum/sort the column, formatted via the cell's numFmt.
			if (field === 'openBalance') return row.amount;

			const value = row[field] ?? '';
			if (value === '') return value;

			// Written as lowercase text rather than a native Excel boolean -
			// Excel always renders a real boolean cell as uppercase TRUE/FALSE
			// regardless of locale or numFmt.
			if (typeof value === 'boolean') return String(value);

			// Age and the "Number of Days Between" columns are stored as
			// ReportRow strings (for the on-screen table), but the export
			// writes them as real numbers so Excel sorts/filters/sums them
			// numerically instead of lexicographically (e.g. "99" > "579").
			if (
				field === 'age' ||
				ReportExportService.EXPORT_DAY_COUNT_FIELDS.has(field)
			) {
				const numericValue = Number(value);
				// OT Sub Date now reflects the ticket's latest submission, so once
				// it's been resubmitted after a dispute, OT Sub Date can fall after
				// OT Disputed Date and flip this to negative. The absolute value is
				// what's meaningful for a resubmitted ticket.
				if (
					field === 'otSubToOtDispDays' &&
					row.otDisputedResubmission
				) {
					return Math.abs(numericValue);
				}
				return numericValue;
			}

			// Date columns are stored as display strings (for the on-screen
			// table), but the export parses them back into real Date cells so
			// Excel can sort/filter them as dates instead of text.
			if (
				typeof value === 'string' &&
				ReportExportService.EXPORT_DATE_FIELDS.has(field)
			) {
				const date = new Date(value);
				return Number.isNaN(date.getTime()) ? value : date;
			}

			return value;
		});
	}

	// OI Status overwrites OT Status: once a row has reached Open Invoice, its
	// OT value is dropped from both the export cell and the tab/colour
	// matching, in favour of OI Status' own colour coding (T1-T4 vs L1-L4).
	private static effectiveOtStatus(row: ReportRow): string | undefined {
		return ReportExportService.effectiveOiStatus(row)
			? undefined
			: row.otStatus;
	}

	// Some rows have an OI Sub/Status/Paid date but no OI Status value from the
	// API. Falls back to inferring the status from whichever date is present
	// so those rows aren't treated as if they never reached Open Invoice.
	private static effectiveOiStatus(row: ReportRow): string | undefined {
		if (row.oiStatus) return row.oiStatus;
		if (row.paidDate || row.oiStatusDate) return 'Approved';
		if (row.oiSubDate) return 'Submitted';
		return undefined;
	}

	private static parseDays(value: string | undefined): number | undefined {
		if (!value) return undefined;
		const days = Number(value);
		return Number.isFinite(days) ? days : undefined;
	}

	private static daysSinceToday(
		value: string | undefined
	): number | undefined {
		if (!value) return undefined;
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return undefined;
		const millisecondsPerDay = 24 * 60 * 60 * 1000;
		return Math.round((Date.now() - date.getTime()) / millisecondsPerDay);
	}

	private static average(values: (number | undefined)[]): number | undefined {
		const defined = values.filter(
			(value): value is number => value !== undefined
		);
		if (!defined.length) return undefined;
		return defined.reduce((sum, value) => sum + value, 0) / defined.length;
	}

	// Company:Job/Location is "<Parent Company> : <Job/Location>"; when every
	// exported row shares the same parent company (a single-customer export),
	// the Summary masthead names that customer instead of a generic label.
	private static resolveCompanyTitle(rows: ReportRow[]): string {
		const companies = new Set(
			rows
				.map((row) =>
					(
						(row.customerJobLocation ?? '').split(' : ')[0] ?? ''
					).trim()
				)
				.filter(Boolean)
		);
		const [onlyCompany] = companies;
		return companies.size === 1 && onlyCompany
			? onlyCompany
			: 'All Customers';
	}

	private static buildRequisitionerSummary(
		rows: ReportRow[]
	): RequisitionerSummary[] {
		const byRequisitioner = new Map<string, RequisitionerSummary>();

		rows.forEach((row) => {
			const requisitioner = (row.requisitioner ?? '').trim();
			if (!requisitioner) return;

			const entry = byRequisitioner.get(requisitioner) ?? {
				requisitioner,
				totalTickets: 0,
				approved: 0,
				submitted: 0,
				disputed: 0,
				openBalance: 0
			};

			entry.totalTickets++;
			entry.openBalance += row.amount;
			if (ReportExportService.isStatus(row.otStatus, 'Approved')) {
				entry.approved++;
			}
			if (ReportExportService.isStatus(row.otStatus, 'Submitted')) {
				entry.submitted++;
			}
			if (ReportExportService.isDisputed(row)) entry.disputed++;

			byRequisitioner.set(requisitioner, entry);
		});

		return Array.from(byRequisitioner.values()).sort(
			(a, b) => b.totalTickets - a.totalTickets
		);
	}

	// Ranked by status severity (most urgent first), then by days outstanding
	// within that severity - matching the source workbook's "Top 10 Invoices
	// Needing Attention" ordering. Approved, Paid and Cancelled items are
	// excluded as already resolved.
	private static readonly ATTENTION_SEVERITY: [
		string,
		(row: ReportRow) => boolean
	][] = [
		['Disputed', (row) => ReportExportService.isDisputed(row)],
		[
			'Submitted',
			(row) =>
				ReportExportService.isStatus(
					ReportExportService.effectiveOtStatus(row),
					'Submitted'
				) ||
				ReportExportService.isStatus(
					ReportExportService.effectiveOiStatus(row),
					'Submitted'
				)
		],
		[
			'Saved and Not Submitted',
			(row) =>
				!ReportExportService.effectiveOiStatus(row) &&
				(!row.otStatus ||
					ReportExportService.isStatus(row.otStatus, 'Saved'))
		]
	];

	private static buildInvoicesNeedingAttention(
		rows: ReportRow[]
	): InvoiceNeedingAttention[] {
		return rows
			.map((row) => {
				const severityEntry =
					ReportExportService.ATTENTION_SEVERITY.find(([, matches]) =>
						matches(row)
					);
				if (!severityEntry) return undefined;

				return {
					severityIndex:
						ReportExportService.ATTENTION_SEVERITY.indexOf(
							severityEntry
						),
					customerJobLocation: row.customerJobLocation ?? '',
					documentNumber: row.documentNumber ?? '',
					status: severityEntry[0],
					daysOutstanding:
						ReportExportService.parseDays(row.age) ?? 0,
					openBalance: row.amount,
					requisitioner: row.requisitioner ?? ''
				};
			})
			.filter(
				(
					row
				): row is InvoiceNeedingAttention & { severityIndex: number } =>
					row !== undefined
			)
			.sort(
				(a, b) =>
					a.severityIndex - b.severityIndex ||
					b.daysOutstanding - a.daysOutstanding
			)
			.slice(0, 10);
	}
}
